// src/hooks/usePrendas.js
import { useState, useCallback } from 'react';
import {
  getPrendasInformation,
  getPrendasDisponibleInformation,
  storePrendas,
  setPrendasStatus,
} from '../services/prendasService';
import Swal from 'sweetalert2';

/**
 * Hook personalizado para manejar operaciones de Prendas
 */
export const usePrendas = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Cargar información de una prenda
   * @param {string} group
   * @param {string} order
   * @param {string} caseType - 'DISPONIBLE' o 'STORED'
   */
  const loadPrendaInfo = useCallback(async (group, order, caseType = 'DISPONIBLE') => {
    setLoading(true);
    setError(null);
    try {
      let data = null;
      if (caseType === 'DISPONIBLE') {
        data = await getPrendasDisponibleInformation(group, order);
      } else {
        data = await getPrendasInformation(group, order, 0);
      }

      if (!data) throw new Error('No se pudieron obtener datos de la prenda');

      setLoading(false);
      return data;
    } catch (err) {
      console.error('[usePrendas] Error al cargar prenda:', err);
      setError(err.message);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error al cargar prenda',
        text: err.message || 'No se pudo cargar la información de la prenda',
        confirmButtonColor: '#ef4444',
      });
      throw err;
    }
  }, []);

  /**
   * Guardar datos de una prenda
   */
  const savePrendaData = useCallback(async (prendaData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await storePrendas(prendaData);
      setLoading(false);
      await Swal.fire({
        icon: 'success',
        title: 'Prenda Guardada',
        text: 'La prenda ha sido guardada correctamente',
        confirmButtonColor: '#ef4444',
        timer: 2000,
      });
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar prenda',
        text: err.message,
        confirmButtonColor: '#ef4444',
      });
      throw err;
    }
  }, []);

  /**
   * Cambiar estado de una prenda
   */
  const changePrendaStatus = useCallback(async (idPrenda, prendaStatus, statusNotes = '', userID = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response = await setPrendasStatus(idPrenda, prendaStatus, statusNotes, userID);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error al cambiar estado',
        text: err.message,
        confirmButtonColor: '#ef4444',
      });
      throw err;
    }
  }, []);

  return {
    loading,
    error,
    loadPrendaInfo,
    savePrendaData,
    changePrendaStatus,
  };
};

export default usePrendas;
